import { FC, RefObject, UIEvent, useEffect, useId, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import type { DocumentCodeSuggestion } from '@web/vin-sessions/types';
import { documentCodeMatch } from './document-code.utils';

interface DocumentCodeMenuProps {
    options: DocumentCodeSuggestion[];
    query: string;
    onSelect: (code: string) => void;
    isLoading: boolean;
    width?: number;
    loadingLabel: string;
    emptyLabel: string;
    headerLabel?: string;
    onReachEnd?: () => void;
    hasMore?: boolean;
    moreLabel?: string;
    moreDocumentsLabel?: (count: number) => string;
    keyboardTarget: RefObject<HTMLElement | null>;
}

const renderCode = (code: string, query: string, matchClass: string) => {
    const range = documentCodeMatch(code, query);
    if (!range) return code;

    return (
        <>
            {code.slice(0, range[0])}
            <span className={matchClass}>{code.slice(range[0], range[1])}</span>
            {code.slice(range[1])}
        </>
    );
};

const useStyles = createUseStyles({
    menu: {
        position: 'absolute',
        top: 0,
        left: 0,
        transform: 'translate3d(0px, 40px, 0px)',
        width: '100%',
        maxHeight: 320,
        overflow: 'auto',
        border: `1px solid ${colors.grayThree}`,
        boxShadow: '0 8px 12px 0 rgba(0, 0, 0, 0.16)',
        borderRadius: 3,
        backgroundColor: colors.white,
        zIndex: 10,
        padding: '4px 0',
    },
    option: {
        appearance: 'none',
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: 'none',
        background: 'transparent',
        padding: '8px 16px',
        cursor: 'pointer',
        '&:hover, &:focus': {
            backgroundColor: colors.blueFour,
            outline: 'none',
        },
    },
    optionActive: {
        backgroundColor: colors.blueFour,
    },
    code: {
        fontSize: '1.7rem',
        lineHeight: '2.4rem',
    },
    detail: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: '1.4rem',
        lineHeight: '2rem',
        color: colors.grayFive,
    },
    detailText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    moreCount: {
        flex: '0 0 auto',
        padding: '0 6px',
        borderRadius: 10,
        backgroundColor: colors.blueFour,
        color: colors.blueOne,
        fontSize: '1.2rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    message: {
        padding: '8px 16px',
        fontSize: '1.5rem',
        color: colors.grayFive,
    },
    match: {
        backgroundColor: 'transparent',
        color: colors.blueOne,
        fontWeight: 700,
    },
    header: {
        padding: '8px 16px',
        fontSize: '1.3rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: colors.grayFive,
        borderBottom: `1px solid ${colors.grayThree}`,
        marginBottom: 4,
    },
});

export const DocumentCodeMenu: FC<DocumentCodeMenuProps> = ({
    options,
    query,
    onSelect,
    isLoading,
    width,
    loadingLabel,
    emptyLabel,
    headerLabel,
    onReachEnd,
    hasMore = false,
    moreLabel,
    moreDocumentsLabel,
    keyboardTarget,
}) => {
    const classes = useStyles();
    const idPrefix = useId();
    const [activeIndex, setActiveIndex] = useState(-1);
    const listRef = useRef<HTMLDivElement>(null);
    const firstCode = options[0]?.code;

    useEffect(() => {
        setActiveIndex(-1);
    }, [query, firstCode]);

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        if (!onReachEnd || !hasMore) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        if (scrollHeight - (scrollTop + clientHeight) < 48) {
            onReachEnd();
        }
    };

    const handleKey = (key: string, preventDefault: () => void) => {
        if (options.length === 0) {
            return;
        }
        if (key === 'ArrowDown' || key === 'ArrowUp') {
            preventDefault();
            const step = key === 'ArrowDown' ? 1 : -1;
            const next =
                activeIndex < 0
                    ? step === 1
                        ? 0
                        : options.length - 1
                    : (activeIndex + step + options.length) % options.length;
            setActiveIndex(next);
            const row = listRef.current?.querySelectorAll('[role="option"]')[next] as HTMLElement | undefined;
            row?.scrollIntoView?.({ block: 'nearest' });
            return;
        }
        if (key === 'Enter' && activeIndex >= 0) {
            preventDefault();
            onSelect(options[activeIndex].code);
        }
    };

    const handleKeyRef = useRef(handleKey);
    useEffect(() => {
        handleKeyRef.current = handleKey;
    });

    useEffect(() => {
        const target = keyboardTarget.current;
        if (!target) {
            return undefined;
        }
        // A native listener is outside React's batching under legacy ReactDOM.render.
        const listener = (event: globalThis.KeyboardEvent) =>
            unstable_batchedUpdates(() => handleKeyRef.current(event.key, () => event.preventDefault()));
        target.addEventListener('keydown', listener);
        return () => target.removeEventListener('keydown', listener);
    }, [keyboardTarget]);

    return (
        <div
            ref={listRef}
            className={classes.menu}
            style={width ? { width } : undefined}
            role="listbox"
            tabIndex={-1}
            onScroll={handleScroll}
        >
            {headerLabel && options.length > 0 && <div className={classes.header}>{headerLabel}</div>}
            {isLoading && options.length === 0 && <div className={classes.message}>{loadingLabel}</div>}
            {!isLoading && options.length === 0 && <div className={classes.message}>{emptyLabel}</div>}
            {options.map((option, index) => (
                <button
                    key={option.code}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    aria-labelledby={`${idPrefix}-${index}-code`}
                    aria-describedby={`${idPrefix}-${index}-detail`}
                    className={`${classes.option} ${index === activeIndex ? classes.optionActive : ''}`}
                    onFocus={() => setActiveIndex(index)}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => onSelect(option.code)}
                >
                    <div className={classes.code}>{renderCode(option.code, query, classes.match)}</div>
                    <div id={`${idPrefix}-${index}-detail`} className={classes.detail}>
                        <span className={classes.detailText}>
                            {[option.publicationType, option.title].filter(Boolean).join(' · ')}
                        </span>
                        {option.moreCount > 0 && moreDocumentsLabel && (
                            <span className={classes.moreCount}>{moreDocumentsLabel(option.moreCount)}</span>
                        )}
                    </div>
                    {/* Unsplit copy of the code: the match highlight would make readers spell it in parts. */}
                    <span id={`${idPrefix}-${index}-code`} hidden>
                        {option.code}
                    </span>
                </button>
            ))}
            {!isLoading && hasMore && moreLabel && <div className={classes.message}>{moreLabel}</div>}
        </div>
    );
};
